/* global propel */

import { __ } from '@wordpress/i18n';
import sanitizeHTML from 'propel/sanitizeHTML';
import { Swiper } from 'swiper';
import { A11y, Navigation, Pagination, Manipulation } from 'swiper/modules';
import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
import getRemBase from 'propel/getRemBase';

const lightboxComponents = document.querySelectorAll('.component-lightbox');
let lightbox;
let lightboxContent;
let currentSlider;
let currentSlidePostIds = [];
let appendOrPrepend = 'append';

const initializeLightbox = (lightboxComponent) => {
	if (lightboxComponent?.detail?.postsWrapper) {
		lightboxComponent = lightboxComponent.detail.postsWrapper;
	}

	const lightboxFigures = lightboxComponent.querySelectorAll(
		'figure:not(.block-library__qa-overlay),[data-lightbox-content],[data-lightbox-coupon-id]'
	);

	if (lightboxFigures.length > 0) {
		lightboxFigures.forEach((lightboxFigure, index) => {
			lightboxFigure.classList.add('component-lightbox__button');
			lightboxFigure.addEventListener('click', openLightbox);
			lightboxFigure.setAttribute(
				'aria-label',
				__('Open Image in Lightbox', 'propel')
			);
			lightboxFigure.setAttribute('data-lightbox-index', index);
		});
	}
};

const initializeLightboxButtons = () => {
	const lightboxButtons = document.querySelectorAll(
		'[data-lightbox-form-id],a[href^="#form-"]'
	);

	lightboxButtons.forEach((lightboxButton) => {
		lightboxButton.classList.add('component-lightbox__button');
		lightboxButton.parentElement.classList.add('component-lightbox');
		lightboxButton.addEventListener('click', openLightbox);
	});
};

const createLightbox = () => {
	lightbox = document.createElement('div');
	lightbox.classList.add('component-lightbox__frame', 'bg-dark');
	lightbox.setAttribute('role', 'dialog');
	lightbox.setAttribute('aria-modal', 'true');

	const lightboxContainer = document.createElement('div');
	lightboxContainer.classList.add('component-lightbox__container', 'swiper');

	lightboxContent = document.createElement('div');
	lightboxContent.classList.add(
		'component-lightbox__content',
		'swiper-wrapper'
	);

	const lightboxCloseOverlay = document.createElement('button');
	lightboxCloseOverlay.classList.add(
		'component-lightbox__close-button-overlay'
	);
	lightboxCloseOverlay.setAttribute('role', 'button');
	lightboxCloseOverlay.setAttribute(
		'aria-label',
		__('Close Lightbox', 'propel')
	);
	lightboxCloseOverlay.addEventListener('click', closeLightbox);

	const lightboxCloseButton = document.createElement('span');
	lightboxCloseButton.classList.add('component-lightbox__close-button');
	lightboxCloseButton.addEventListener('click', closeLightbox);

	const lightboxButtonPrev = document.createElement('button');
	lightboxButtonPrev.classList.add('swiper-button-prev');
	lightboxButtonPrev.setAttribute('role', 'button');
	lightboxButtonPrev.innerText = __('Previous slide', 'propel');
	lightboxContainer.appendChild(lightboxButtonPrev);

	const lightboxButtonNext = document.createElement('button');
	lightboxButtonNext.classList.add('swiper-button-next');
	lightboxButtonNext.setAttribute('role', 'button');
	lightboxButtonNext.innerText = __('Next slide', 'propel');
	lightboxContainer.appendChild(lightboxButtonNext);

	const lightboxPagination = document.createElement('div');
	lightboxPagination.classList.add('swiper-pagination');
	lightboxContainer.appendChild(lightboxPagination);

	lightboxCloseOverlay.appendChild(lightboxCloseButton);
	lightboxContainer.appendChild(lightboxContent);
	lightbox.appendChild(lightboxContainer);
	lightbox.appendChild(lightboxCloseOverlay);
	document.body.appendChild(lightbox);
};

const openLightbox = (e) => {
	e.preventDefault();

	if (!lightbox) {
		createLightbox();
	}

	let currentSlideIndex = 1;

	if (e.currentTarget.hasAttribute('data-lightbox-index')) {
		currentSlideIndex = e.currentTarget.getAttribute('data-lightbox-index');
	}

	const lightboxComponent = e.currentTarget.closest('.component-lightbox');

	if (lightboxComponent) {
		let lightboxFigures = [];

		if (e.currentTarget?.dataset?.lightboxCouponId) {
			lightboxFigures = lightboxComponent.querySelectorAll(
				`.component-lightbox__button[data-lightbox-coupon-id="${e.currentTarget.dataset.lightboxCouponId}"]`
			);
		} else {
			lightboxFigures = lightboxComponent.querySelectorAll(
				'.component-lightbox__button'
			);
		}

		if (lightboxFigures.length > 0) {
			lightboxContent.innerHTML = '';
			currentSlidePostIds = [];

			lightboxFigures.forEach((lightboxFigure) => {
				const slideContent = getSlideContent(lightboxFigure);

				lightboxContent.appendChild(slideContent);

				if (lightboxFigure.hasAttribute('data-post-id')) {
					currentSlidePostIds.push(
						parseInt(lightboxFigure.getAttribute('data-post-id'))
					);
				}
			});

			checkAdditionalCloseButtons();

			lightbox.setAttribute('data-slide-count', lightboxFigures.length);

			const settings = {
				loop: lightboxFigures.length > 1 ? true : false,
				grabCursor: true,
				spaceBetween: 32,
				slidesPerView: 1,
				initialSlide: currentSlideIndex,
				modules: [A11y, Navigation, Pagination, Manipulation],
				a11y: {
					itemRoleDescriptionMessage: 'slide',
				},
				navigation: {
					prevEl: '.swiper-button-prev',
					nextEl: '.swiper-button-next',
				},
				pagination: {
					el: '.swiper-pagination',
					clickable: true,
					dynamicBullets: true,
					dynamicMainBullets: 5,
				},
				on: {
					slideChange,
				},
			};

			if (
				lightboxComponent.classList.contains(
					'block-filter-top__posts'
				) ||
				lightboxComponent.classList.contains('block-filter-side__posts')
			) {
				settings.loop = false;

				settings.on.reachBeginning = () => {
					appendOrPrepend = 'prepend';
					currentSlider.allowSlidePrev = false;
					currentSlider.params.allowSlidePrev = false;
					currentSlider.navigation.prevEl.classList.add(
						'swiper-button--loading'
					);
					currentSlider.navigation.prevEl.disabled = true;

					const reachEndEvent = new CustomEvent(
						'propel-lightbox-reach-beginning',
						{
							detail: {
								lightboxComponent,
								page:
									parseInt(
										currentSlider.slides[
											currentSlider.activeIndex
										].firstElementChild.getAttribute(
											'data-page'
										)
									) - 1,
							},
						}
					);
					window.dispatchEvent(reachEndEvent);
					slideChange(currentSlider);
				};

				settings.on.reachEnd = () => {
					appendOrPrepend = 'append';
					currentSlider.allowSlideNext = false;
					currentSlider.params.allowSlideNext = false;
					currentSlider.navigation.nextEl.classList.add(
						'swiper-button--loading'
					);
					currentSlider.navigation.nextEl.disabled = true;

					const reachEndEvent = new CustomEvent(
						'propel-lightbox-reach-end',
						{
							detail: {
								lightboxComponent,
								page:
									parseInt(
										currentSlider.slides[
											currentSlider.activeIndex
										].firstElementChild.getAttribute(
											'data-page'
										)
									) + 1,
							},
						}
					);
					window.dispatchEvent(reachEndEvent);
					slideChange(currentSlider);
				};
			}

			if (1 === lightboxFigures.length) {
				settings.navigation = false;
				settings.pagination = false;
				settings.allowTouchMove = false;
				settings.loop = false;

				const lightboxFigure = lightboxContent.querySelector(
					'.component-video[data-embed-url]'
				);

				if (lightboxFigure) {
					playVideo(lightboxFigure);
				}
			}

			currentSlider = new Swiper(
				'.component-lightbox__container',
				settings
			);

			lightbox.classList.add('active');

			if (
				lightbox.classList.contains(
					'component-lightbox__frame--has-coupon-close-buttons'
				)
			) {
				const scrollableItems = lightbox.querySelectorAll(
					'.coupon-card-lightbox__content, .coupon-card-lightbox__locations'
				);

				scrollableItems.forEach(function (scrollableItem) {
					const storedRequestAnimationFrame =
						window.requestAnimationFrame;
					window.requestAnimationFrame = () => 42;

					disableBodyScroll(scrollableItem, {
						reserveScrollBarGap: true,
					});
					window.requestAnimationFrame = storedRequestAnimationFrame;
				});
			} else {
				disableBodyScroll(lightbox, {
					reserveScrollBarGap: true,
				});
			}

			document.addEventListener('keydown', keydown);

			setTimeout(function () {
				lightbox.classList.add('visible');
			}, 1);

			const lightboxOpened = new CustomEvent('propel-lightbox-opened', {
				detail: {
					lightboxComponent,
					event: e,
				},
			});
			window.dispatchEvent(lightboxOpened);
		}
	}
};

const getSlideContent = (lightboxFigure) => {
	let newLightboxFigure;
	const slideContent = document.createElement('div');
	slideContent.classList.add('component-lightbox__slide', 'swiper-slide');

	if (lightboxFigure.hasAttribute('data-lightbox-content')) {
		const slideContentString = JSON.parse(
			lightboxFigure.getAttribute('data-lightbox-content')
		);

		slideContent.innerHTML = sanitizeHTML(slideContentString);

		if (lightboxFigure.hasAttribute('data-page')) {
			slideContent.firstElementChild.setAttribute(
				'data-page',
				lightboxFigure.getAttribute('data-page')
			);
		}

		newLightboxFigure = slideContent.querySelector('[data-embed-url]');
	} else if (lightboxFigure.hasAttribute('data-lightbox-coupon-id')) {
		const couponId = lightboxFigure.getAttribute('data-lightbox-coupon-id');
		const couponData = window[`fsaCouponCardData_${couponId}`] || {};

		if (couponData) {
			slideContent.innerHTML = sanitizeHTML(couponData);

			if (lightboxFigure.hasAttribute('data-page')) {
				slideContent.firstElementChild.setAttribute(
					'data-page',
					lightboxFigure.getAttribute('data-page')
				);
			}

			newLightboxFigure = slideContent.querySelector('[data-embed-url]');
		}
	} else if (
		lightboxFigure.hasAttribute('data-lightbox-form-id') ||
		(lightboxFigure.hasAttribute('href') &&
			lightboxFigure.getAttribute('href').startsWith('#form-'))
	) {
		let formId;

		if (lightboxFigure.hasAttribute('data-lightbox-form-id')) {
			formId = lightboxFigure.getAttribute('data-lightbox-form-id');
		} else {
			const href = lightboxFigure.getAttribute('href');
			formId = href.replace('#form-', '');
		}

		const locationId = lightboxFigure?.getAttribute('data-location-id');

		slideContent.innerHTML = '';

		let lightboxURL = `${propel.siteUrl}/forms/${formId}?form_page_url=${encodeURIComponent(window.location.href)}`;

		if (locationId) {
			lightboxURL += `&location_id=${locationId}`;
		}

		const currentParams = window.location.search;

		if (currentParams) {
			lightboxURL +=
				(lightboxURL.includes('?') ? '&' : '?') +
				currentParams.slice(1);
		}

		const iframeWrapper = document.createElement('div');
		const iframe = document.createElement('iframe');
		iframe.src = lightboxURL;
		iframeWrapper.classList.add(
			'component-lightbox__form-iframe',
			'component-lightbox__form-iframe--loading'
		);
		iframe.onload = function () {
			try {
				const iframeDocument =
					iframe.contentDocument || iframe.contentWindow.document;
				if (iframeDocument && iframeDocument.body) {
					const contentHeight = iframeDocument.body.scrollHeight;
					iframe.style.height = `${contentHeight / getRemBase()}rem`;
					iframeWrapper.classList.remove(
						'component-lightbox__form-iframe--loading'
					);
				}
			} catch (err) {}
		};
		iframeWrapper.appendChild(iframe);
		slideContent.appendChild(iframeWrapper);
	} else {
		newLightboxFigure = lightboxFigure.cloneNode(true);
		newLightboxFigure.classList.remove('component-lightbox__button');
		newLightboxFigure.removeAttribute('aria-label');
		slideContent.appendChild(newLightboxFigure);
	}

	return slideContent;
};

const keydown = (e) => {
	if ('Escape' === e.key) {
		closeLightbox(e);
	} else if ('ArrowRight' === e.key) {
		currentSlider.slideNext();
	} else if ('ArrowLeft' === e.key) {
		currentSlider.slidePrev();
	}
};

const closeLightbox = (e) => {
	e.preventDefault();

	lightbox.classList.remove('visible');

	setTimeout(function () {
		lightbox.classList.remove('active');
		clearAllBodyScrollLocks();

		currentSlider.destroy(true);
		lightboxContent.innerHTML = '';
	}, 400);

	document.removeEventListener('keydown', closeLightbox);
};

const slideChange = (swiperInstance) => {
	if (
		!swiperInstance.slides ||
		!swiperInstance.slides[swiperInstance.activeIndex]
	) {
		return;
	}

	const currentSlide = swiperInstance.slides[swiperInstance.activeIndex];
	const lightboxFigure = currentSlide.querySelector(
		'.component-video[data-embed-url]'
	);

	if (lightboxFigure) {
		playVideo(lightboxFigure);
	} else if (swiperInstance) {
		stopVideos(swiperInstance);
	}
};

const playVideo = (lightboxFigure) => {
	window.dispatchEvent(
		new CustomEvent('propel-play-video', {
			detail: {
				lightboxVideoComponent: lightboxFigure,
				lightboxVideoContent: lightboxFigure,
			},
		})
	);
};

const stopVideos = () => {
	window.dispatchEvent(new Event('propel-stop-videos'));
};

const possiblyRenderMoreSlides = (e) => {
	if (
		!currentSlider ||
		currentSlider?.destroyed ||
		!e?.detail?.postsWrapper
	) {
		return;
	}

	if (!currentSlider.params) {
		currentSlider.params = {};
	}

	currentSlider.allowSlidePrev = true;
	currentSlider.params.allowSlidePrev = true;
	currentSlider.navigation.prevEl.classList.remove('swiper-button--loading');
	currentSlider.navigation.prevEl.disabled = false;
	currentSlider.allowSlideNext = true;
	currentSlider.params.allowSlideNext = true;
	currentSlider.navigation.nextEl.classList.remove('swiper-button--loading');
	currentSlider.navigation.nextEl.disabled = false;

	let lightboxFigures = e.detail.postsWrapper.querySelectorAll(
		'.component-lightbox__button'
	);

	if (lightboxFigures.length > 0) {
		if ('prepend' === appendOrPrepend) {
			lightboxFigures = Array.from(lightboxFigures).reverse();
		}

		lightboxFigures.forEach((lightboxFigure) => {
			if (!lightboxFigure.hasAttribute('data-post-id')) {
				return;
			}

			const postId = parseInt(
				lightboxFigure.getAttribute('data-post-id')
			);

			if (currentSlider?.slides?.length > 0) {
				for (
					let index = 0;
					index < currentSlider.slides.length;
					index++
				) {
					if (
						!currentSlider.slides[
							index
						]?.firstElementChild.hasAttribute('data-post-id')
					) {
						continue;
					}

					if (
						postId ===
						parseInt(
							currentSlider.slides[
								index
							].firstElementChild.getAttribute('data-post-id')
						)
					) {
						return;
					}
				}
			}

			const slideContent = getSlideContent(lightboxFigure);

			if ('prepend' === appendOrPrepend) {
				currentSlider.prependSlide(slideContent);
			} else {
				currentSlider.appendSlide(slideContent);
			}

			currentSlidePostIds.push(postId);
		});

		currentSlider.update();

		checkAdditionalCloseButtons();
	}
};

const checkAdditionalCloseButtons = () => {
	const additionalCloseButtons = document.querySelectorAll(
		'.coupon-card-lightbox__close-button:not(.initialized)'
	);

	if (0 === additionalCloseButtons.length) {
		return;
	}

	const currentLightbox = additionalCloseButtons[0].closest(
		'.component-lightbox__frame'
	);

	if (currentLightbox) {
		currentLightbox.classList.add(
			'component-lightbox__frame--has-coupon-close-buttons'
		);
	}

	additionalCloseButtons.forEach((button) => {
		button.classList.add('initialized');

		button.addEventListener('click', (e) => {
			e.preventDefault();
			closeLightbox(e);
		});
	});
};

const handledRenderedPageSlides = () => {
	if (!currentSlider) {
		return;
	}

	if (!currentSlider.params) {
		currentSlider.params = {};
	}

	currentSlider.allowSlidePrev = true;
	currentSlider.params.allowSlidePrev = true;
	currentSlider.navigation.prevEl.classList.remove('swiper-button--loading');
	currentSlider.navigation.prevEl.disabled = false;
	currentSlider.allowSlideNext = true;
	currentSlider.params.allowSlideNext = true;
	currentSlider.navigation.nextEl.classList.remove('swiper-button--loading');
	currentSlider.navigation.nextEl.disabled = false;
};

if (lightboxComponents.length > 0) {
	lightboxComponents.forEach((lightboxComponent) => {
		initializeLightbox(lightboxComponent);
	});
}

initializeLightboxButtons();

window.addEventListener('propel-filters-render-posts', initializeLightbox);
window.addEventListener(
	'propel-filters-render-posts',
	initializeLightboxButtons
);
window.addEventListener(
	'propel-filters-render-posts',
	possiblyRenderMoreSlides
);
window.addEventListener(
	'propel-filters-page-already-rendered',
	handledRenderedPageSlides
);
